import { Fragment } from 'react'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from './ui/drawer'
import { MenuItem } from './ui/menu-item'
import { EmptyState } from './EmptyState'
import { ClipboardCheck, X } from '@/components/icons'
import { useMyDraftTasks } from '@/lib/hooks/task'
import { hapticLight } from '@/lib/haptics'

dayjs.extend(relativeTime)

interface NewTasksDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NewTasksDrawer({ open, onOpenChange }: NewTasksDrawerProps) {
  const navigate = useNavigate()
  const { data: tasks } = useMyDraftTasks()

  const handleRowTap = (taskId: string) => {
    hapticLight()
    onOpenChange(false)
    navigate({ to: '/task/$taskId', params: { taskId } })
  }

  const items = tasks ?? []

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="flex flex-row items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-content-brand-bold" />
            <DrawerTitle>New Tasks</DrawerTitle>
          </div>
          <DrawerClose asChild>
            <button
              type="button"
              aria-label="Close"
              className="rounded-full p-1 text-content-primary-subtle"
            >
              <X className="w-5 h-5" />
            </button>
          </DrawerClose>
        </DrawerHeader>

        <div className="px-4 pb-6">
          {items.length === 0 ? (
            <EmptyState
              icon={ClipboardCheck}
              title="All caught up"
              description="No new tasks assigned"
            />
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((task, index) => {
                const route = [
                  task.tripLeg?.departureAirportIcao,
                  task.tripLeg?.arrivalAirportIcao,
                ]
                  .filter(Boolean)
                  .join(' → ')
                return (
                  <Fragment key={task.id}>
                    {index > 0 && <hr className="border-border-L1" />}
                    <MenuItem
                      type="button"
                      size="large"
                      onClick={() => handleRowTap(task.id)}
                      secondaryText={
                        <span className="flex flex-col gap-1">
                          {(route || task.tripLeg?.trip?.tripName) && (
                            <span className="text-sm text-content-primary-subtle">
                              {[task.tripLeg?.trip?.tripName, route]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                          <span className="text-xs font-semibold text-content-primary-subtle">
                            {dayjs(task.createdAt).fromNow()}
                          </span>
                        </span>
                      }
                    >
                      <span className="inline-flex items-center gap-[7px]">
                        {task.name}
                        <span className="size-[9px] rounded-full bg-content-info-bold shrink-0" />
                      </span>
                    </MenuItem>
                  </Fragment>
                )
              })}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
